import {useCan} from '@/shared/hooks/use-permissions'
import {useConfirmDeleteSupportiveMedication} from '@/modules/drug-catalog/supportive-medications/api'
import {SupportiveMedicationSummary} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/supportive-medication-summary'
import {SupportiveMedicationDrawerTabsList} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/tabs-list'
import {AboutTab} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/tabs/about'
import {HistoryTab} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/tabs/activity'
import {MedicationProfilesTab} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/tabs/medication-profiles'
import {TradeNamesTab} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/tabs/trade-names'
import {getSupportiveMedicationDrawerId} from '@/modules/drug-catalog/supportive-medications/components/supportive-medication-drawer/util'
import {isSupportiveMedicationDeletable} from '@/modules/drug-catalog/supportive-medications/functions'
import {drawer} from '@cypro/ui/components'
import {DrawerBody, DrawerFooter, DrawerHeader} from '@cypro/ui/components'
import {DeleteResourceButton} from '@cypro/ui/components/ui/delete-resource-button'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {useSupportiveMedicationDrugContext} from '@/shared/contexts/supportive-medication-drug'
import {toaster} from '@cypro/ui/toaster'
import {Drawer, Tabs} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'

export function SupportiveMedicationDrawer() {
  const canManageCatalogue = useCan('catalogue.manage')
  const {t} = useTranslate('common')
  const {t: tSupportive} = useTranslate('supportive-medications')
  const {supportiveMedicationDrug} = useSupportiveMedicationDrugContext()
  const deleteSupportiveMedication = useConfirmDeleteSupportiveMedication()

  async function onDelete() {
    try {
      await deleteSupportiveMedication({id: supportiveMedicationDrug.id})
      const drawerId = getSupportiveMedicationDrawerId(supportiveMedicationDrug.id)
      drawer.close(drawerId)
      toaster.success({title: t('success'), description: t('resource_deleted_successfully')})
    } catch {}
  }

  const canDelete = isSupportiveMedicationDeletable(supportiveMedicationDrug)
  const deleteBtn = <DeleteResourceButton onClick={onDelete} disabled={!canDelete} />

  return (
    <>
      <DrawerHeader>
        <Drawer.Title>{t('supportive_medication_details')}</Drawer.Title>
      </DrawerHeader>

      <DrawerBody>
        <SupportiveMedicationSummary />

        <Tabs.Root defaultValue="about" spaceY="5" lazyMount>
          <SupportiveMedicationDrawerTabsList />

          <AboutTab />
          <MedicationProfilesTab />
          <TradeNamesTab />
          <HistoryTab />
        </Tabs.Root>
      </DrawerBody>

      {canManageCatalogue && (
        <DrawerFooter display="flex" justifyContent="space-between">
          {/* // TODO: implement active substances v2 */}
          {!canDelete && <Tooltip content={tSupportive('cannot_delete_message', {tradeNames: 0})}>{deleteBtn}</Tooltip>}
          {canDelete && deleteBtn}
          <LinkButton
            to="/configuration/drug-catalog/supportive-medications/new"
            search={{drugId: supportiveMedicationDrug.id}}
            colorPalette="cyan"
            data-e2e="edit-substance"
          >
            {t('edit')}
          </LinkButton>
        </DrawerFooter>
      )}
    </>
  )
}
